import { useEffect, useMemo, useState } from 'react';
import { nikkeCatalogSearchText } from '../../nikke-catalog/domain/nikkeCatalog.js';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import { loadUnionMemberDetail } from '../../union-roster/api/unionMemberDetailApi.js';
import { useUnionRoster } from '../../union-roster/hooks/useUnionRoster.js';
import { formatPersonalNikkeNumber, personalNikkeSuperiorCodeValue } from '../domain/personalNikke.js';
import { usePersonalNikke } from '../hooks/usePersonalNikke.js';
import { PersonalNikkeCard } from './PersonalNikkeCard.jsx';

const ELEMENTS = [['all', '전체'], ['작열', '작열'], ['전격', '전격'], ['수냉', '수냉'], ['풍압', '풍압'], ['철갑', '철갑']];
const BURSTS = [['all', '전체'], ['1', 'I'], ['2', 'II'], ['3', 'III'], ['A', 'A']];
function normalizedSearch(value) { return String(value || '').toLocaleLowerCase('ko-KR').replace(/\s+/g, ''); }
function burstValue(item) {
  if (item?.burst) return String(item.burst);
  if (item?.burstCode === 'Step1') return '1';
  if (item?.burstCode === 'Step2') return '2';
  if (item?.burstCode === 'Step3') return '3';
  if (item?.burstCode === 'AllStep') return 'A';
  return '';
}
function formatSyncedAt(value) {
  if (!value) return '아직 동기화하지 않음';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
}
function nextFilterValue(current, selected) {
  if (selected === 'all') return 'all';
  return current === selected ? 'all' : selected;
}

export function PersonalNikkePage({ mobile = false, canViewUnionMembers = false }) {
  const personal = usePersonalNikke();
  const roster = useUnionRoster();
  const [selectedOpenid, setSelectedOpenid] = useState('self');
  const [memberData, setMemberData] = useState(null);
  const [memberDataOpenid, setMemberDataOpenid] = useState('');
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState('');
  const [query, setQuery] = useState('');
  const [element, setElement] = useState('all');
  const [burst, setBurst] = useState('all');
  const [overloadOnly, setOverloadOnly] = useState(false);
  const [sort, setSort] = useState('combat');
  const isSelf = selectedOpenid === 'self';
  const canViewOthers = Boolean(canViewUnionMembers);

  useEffect(() => {
    if (canViewOthers || selectedOpenid === 'self') return;
    setSelectedOpenid('self');
    setMemberData(null);
    setMemberDataOpenid('');
    setMemberError('');
    setMemberLoading(false);
  }, [canViewOthers, selectedOpenid]);

  useEffect(() => {
    let active = true;
    if (isSelf || !canViewOthers) {
      setMemberData(null);
      setMemberDataOpenid('');
      setMemberError('');
      setMemberLoading(false);
      return () => { active = false; };
    }
    setMemberData(null);
    setMemberDataOpenid('');
    setMemberLoading(true);
    setMemberError('');
    loadUnionMemberDetail(selectedOpenid).then((detail) => {
      if (active) {
        setMemberData(detail);
        setMemberDataOpenid(selectedOpenid);
      }
    }).catch((reason) => {
      if (active) {
        setMemberData(null);
        setMemberDataOpenid('');
        setMemberError(reason instanceof Error ? reason.message : '멤버 니케 정보를 불러오지 못했습니다.');
      }
    }).finally(() => { if (active) setMemberLoading(false); });
    return () => { active = false; };
  }, [canViewOthers, isSelf, selectedOpenid]);

  const selectedMember = roster.members.find((member) => member.memberOpenid === selectedOpenid) || null;
  const selectedMemberData = memberDataOpenid === selectedOpenid ? memberData : null;
  const view = isSelf ? personal : {
    profile: selectedMemberData?.profile || null,
    characters: selectedMemberData?.characters || [],
    loading: memberLoading,
    error: memberError || selectedMemberData?.personalError || '',
    syncing: false,
    sync: null,
  };

  const characters = useMemo(() => {
    const needle = normalizedSearch(query);
    const filtered = (view.characters || []).filter((entry) => {
      const item = entry.catalogItem;
      if (!item) return false;
      if (element !== 'all' && item.element !== element) return false;
      if (burst !== 'all' && burstValue(item) !== burst) return false;
      if (overloadOnly && !entry.overloadCount) return false;
      if (!needle) return true;
      return normalizedSearch(nikkeCatalogSearchText(item)).includes(needle);
    });
    return filtered.sort((left, right) => {
      if (sort === 'name') return left.catalogItem.name.localeCompare(right.catalogItem.name, 'ko-KR');
      if (sort === 'overload') return right.overloadCount - left.overloadCount || right.combat - left.combat;
      if (sort === 'superior') return personalNikkeSuperiorCodeValue(right) - personalNikkeSuperiorCodeValue(left) || right.combat - left.combat || left.catalogItem.name.localeCompare(right.catalogItem.name, 'ko-KR');
      return right.combat - left.combat || personalNikkeSuperiorCodeValue(right) - personalNikkeSuperiorCodeValue(left);
    });
  }, [burst, element, overloadOnly, query, sort, view.characters]);

  const prefix = mobile ? 'mobile' : 'desktop';
  const headingName = isSelf ? '내 니케' : `${selectedMember?.nickname || '멤버'} 니케`;
  return <section className={`${prefix}-personal-nikke personal-nikke-page`} aria-label="니케 정보">
    <header className="personal-nikke-heading"><div><p className="section-eyebrow">MY NIKKE</p><h1>{headingName}</h1><p>{isSelf ? '인증된 BlablaLink 계정의 보유 니케와 오버로드 정보를 확인합니다.' : '선택한 유니온 멤버가 동기화한 보유 니케와 오버로드 정보를 확인합니다.'}</p></div>{isSelf ? <button type="button" className="personal-nikke-sync" onClick={personal.sync} disabled={personal.syncing}>{personal.syncing ? '동기화 중…' : '정보 동기화'}</button> : null}</header>

    {canViewOthers ? <section className="personal-nikke-member-selector" aria-label="유니온 멤버 니케 선택"><label><span>조회 대상</span><span className="personal-nikke-member-select-wrap"><select value={selectedOpenid} onChange={(event) => { setSelectedOpenid(event.target.value); setMemberData(null); setMemberDataOpenid(''); setMemberError(''); setQuery(''); }}><option value="self">내 계정</option>{roster.members.filter((member) => !member.isSelf).map((member) => <option key={member.memberOpenid} value={member.memberOpenid}>{member.nickname}</option>)}</select></span></label></section> : null}

    {view.profile ? <section className="personal-nikke-profile"><div><span>COMMANDER</span><strong>{view.profile.nickname || selectedMember?.nickname || '닉네임 확인 완료'}</strong><small>최근 동기화 {formatSyncedAt(view.profile.syncedAt)}</small></div><dl><div><dt>지휘관 레벨</dt><dd>{formatPersonalNikkeNumber(view.profile.playerLevel)}</dd></div><div><dt>대표 전투력</dt><dd>{formatPersonalNikkeNumber(view.profile.teamCombat)}</dd></div><div><dt>보유 니케</dt><dd>{formatPersonalNikkeNumber(view.profile.ownedCount)}명</dd></div><div><dt>보유 코스튬</dt><dd>{formatPersonalNikkeNumber(view.profile.costumeCount)}개</dd></div></dl></section> : null}

    {view.error ? <p className="personal-nikke-notice error" role="alert">{view.error}</p> : null}
    {isSelf && personal.notice ? <p className="personal-nikke-notice success" role="status">{personal.notice}</p> : null}
    <section className="personal-nikke-toolbar"><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="니케 이름·별명 검색" aria-label="니케 검색" /><select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="정렬"><option value="superior">우월코드순</option><option value="combat">전투력순</option><option value="overload">오버로드순</option><option value="name">이름순</option></select><button type="button" className={overloadOnly ? 'active' : ''} aria-pressed={overloadOnly} onClick={() => setOverloadOnly((value) => !value)}>오버로드만</button></section>
    <section className="personal-nikke-filter-row" aria-label="속성 필터">{ELEMENTS.map(([value, label]) => <button type="button" className={element === value ? 'active' : ''} key={value} onClick={() => setElement(nextFilterValue(element, value))}>{value === 'all' ? null : <ElementIcon element={value} />} {label}</button>)}</section>
    <section className="personal-nikke-filter-row compact" aria-label="버스트 필터">{BURSTS.map(([value, label]) => <button type="button" className={burst === value ? 'active' : ''} key={value} onClick={() => setBurst(nextFilterValue(burst, value))}>{label}</button>)}<strong>{characters.length}명</strong></section>
    {view.loading ? <div className="personal-nikke-state" role="status" aria-busy="true"><span /><p>니케 정보를 불러오는 중…</p></div> : null}
    {!view.loading && !view.profile ? <div className="personal-nikke-state"><strong>동기화된 니케 정보가 없습니다.</strong><p>{isSelf ? 'BlablaLink 인증을 완료한 뒤 정보 동기화를 눌러주세요.' : '해당 멤버가 아직 니케 정보를 동기화하지 않았습니다.'}</p></div> : null}
    {!view.loading && view.profile && !characters.length ? <div className="personal-nikke-state"><strong>조건에 맞는 니케가 없습니다.</strong><p>검색어나 필터를 변경해주세요.</p></div> : null}
    {characters.length ? <div className="personal-nikke-grid">{characters.map((entry) => <PersonalNikkeCard personal={entry} mobile={mobile} key={entry.nameCode} />)}</div> : null}
  </section>;
}
